/**
 * Font Picker Component
 * Modal for selecting number plate font style
 * Adapted from prototype for Shopify theme integration
 */

class BundleFontPicker {
  constructor(config) {
    this.id = config.id || 'font-picker-modal';
    this.fonts = config.fonts || this.getDefaultFonts();
    this.selectedId = config.selectedId || 'standard';
    this.onSelect = config.onSelect || null;
  }

  /**
   * Get default font options
   */
  getDefaultFonts() {
    return [
      {
        id: 'standard',
        name: 'Standard',
        sample: 'RIIROO',
        price: 0,
        description: 'Legal UK plate font (Charles Wright)'
      },
      {
        id: '3d-effect',
        name: '3D Effect',
        sample: 'RIIROO',
        price: 2.99,
        description: 'Embossed look with depth'
      },
      {
        id: 'bold',
        name: 'Bold',
        sample: 'RIIROO',
        price: 1.99,
        description: 'Heavier weight characters'
      },
      {
        id: 'slim',
        name: 'Slim',
        sample: 'RIIROO',
        price: 1.99,
        description: 'Condensed style characters'
      }
    ];
  }

  /**
   * Format price for display
   */
  formatPrice(price) {
    if (price === 0) {
      return 'Included';
    }
    return `+£${price.toFixed(2)}`;
  }

  /**
   * Render font option row
   */
  renderFontOption(font) {
    const isSelected = font.id === this.selectedId;
    const isFree = font.price === 0;

    return `
      <div class="bundle-font-picker__option ${isSelected ? 'bundle-font-picker__option--selected' : ''}"
           data-font-id="${font.id}"
           data-action="select-font">
        <div class="bundle-font-picker__option-content">
          <div class="bundle-font-picker__radio">
            <div class="bundle-font-picker__radio-outer">
              ${isSelected ? '<div class="bundle-font-picker__radio-inner"></div>' : ''}
            </div>
          </div>

          <div class="bundle-font-picker__info">
            <div class="bundle-font-picker__name">${font.name}</div>
            <div class="bundle-font-picker__sample" data-font-style="${font.id}">${font.sample}</div>
            ${font.description ? `<div class="bundle-font-picker__description">${font.description}</div>` : ''}
          </div>
        </div>

        <div class="bundle-font-picker__price ${isFree ? 'bundle-font-picker__price--free' : ''}">
          ${this.formatPrice(font.price)}
        </div>
      </div>
    `;
  }

  /**
   * Render modal HTML
   */
  render() {
    return `
      <div class="bundle-modal-overlay" id="${this.id}" style="display: none;" data-modal-id="${this.id}">
        <div class="bundle-modal-backdrop" data-action="dismiss-modal" data-modal-id="${this.id}"></div>
        <div class="bundle-modal bundle-modal--bottom-sheet">
          <div class="bundle-modal__header">
            <h2 class="bundle-modal__title">Choose Font Style</h2>
            <button class="bundle-modal__close" data-action="dismiss-modal" data-modal-id="${this.id}" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div class="bundle-modal__content">
            <div class="bundle-font-picker">
              <p class="bundle-font-picker__description-text">
                Select the font style for your number plate text. Premium fonts add character and personality to your plate.
              </p>

              <div class="bundle-font-picker__options">
                ${this.fonts.map(font => this.renderFontOption(font)).join('')}
              </div>
            </div>
          </div>

          <div class="bundle-modal__footer">
            <button class="button button--primary" is="hover-button" data-action="confirm-font" data-modal-id="${this.id}">
              <span class="btn-fill" data-fill></span>
              <span class="btn-text">Confirm Selection</span>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Show font picker modal
   */
  static show(modalId) {
    BundleModal.show(modalId);
  }

  /**
   * Hide font picker modal
   */
  static hide(modalId) {
    BundleModal.hide(modalId);
  }

  /**
   * Get currently selected font ID from modal
   */
  static getSelectedFont(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) return null;

    const selectedOption = modal.querySelector('.bundle-font-picker__option--selected');
    return selectedOption ? selectedOption.dataset.fontId : null;
  }

  /**
   * Set selected font in modal
   */
  static setSelectedFont(modalId, fontId) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    // Remove previous selection
    const previousSelected = modal.querySelector('.bundle-font-picker__option--selected');
    if (previousSelected) {
      previousSelected.classList.remove('bundle-font-picker__option--selected');
      const radio = previousSelected.querySelector('.bundle-font-picker__radio-inner');
      if (radio) radio.remove();
    }

    // Add new selection
    const newSelected = modal.querySelector(`[data-font-id="${fontId}"]`);
    if (newSelected) {
      newSelected.classList.add('bundle-font-picker__option--selected');
      const radioOuter = newSelected.querySelector('.bundle-font-picker__radio-outer');
      if (radioOuter) {
        radioOuter.innerHTML = '<div class="bundle-font-picker__radio-inner"></div>';
      }
    }
  }

  /**
   * Initialize font picker event listeners
   */
  static init(modalId, onSelect) {
    const modal = document.getElementById(modalId);
    if (!modal) return;

    // Handle font option clicks
    modal.addEventListener('click', (e) => {
      const optionElement = e.target.closest('[data-action="select-font"]');
      if (optionElement) {
        const fontId = optionElement.dataset.fontId;
        BundleFontPicker.setSelectedFont(modalId, fontId);
      }

      // Handle confirm button
      if (e.target.closest('[data-action="confirm-font"]')) {
        const selectedFontId = BundleFontPicker.getSelectedFont(modalId);
        if (selectedFontId && onSelect) {
          onSelect(selectedFontId);
        }
        BundleFontPicker.hide(modalId);
      }

      // Handle dismiss actions
      if (e.target.closest('[data-action="dismiss-modal"]')) {
        BundleFontPicker.hide(modalId);
      }
    });
  }
}

// Export to global scope
window.BundleFontPicker = BundleFontPicker;
